import React, { useEffect, useMemo } from 'react'

import { StylesProvider, ThemeProvider } from '@material-ui/core/styles'
import { StoreProvider } from 'easy-peasy'
import { Router } from 'wouter'
import staticLocationHook from 'wouter/static-location'
import browserLocationHook from 'wouter/use-location'

import ErrorBoundary from 'artkive/components/ErrorBoundary'
import { AppConfigContext } from 'artkive/contexts/AppConfigContext'
import withHydration from 'artkive/hoc/withHydration'
import withKlaviyoFormCatch from 'artkive/hoc/withKlaviyoFormCatch'
import withStyledSsr from 'artkive/hoc/withStyledSsr'
import store from 'artkive/stores/ecom/ecom.store'
import muiTheme from 'artkive/utils/muiTheme'

const isSSR = typeof window === 'undefined'

/**
 * https://developers.facebook.com/community/threads/320013549791141/
 *
 * Facebook script is not loaded properly inside FB/Instagram WebView browser. The callback called before init.
 */
if (!isSSR) {
  window._AutofillCallbackHandler = window._AutofillCallbackHandler || function () {}
}

const EMPTY_OBJECT = {}

const withApp = (Component, options = {}) => {
  const WithKlaviyoForm = withKlaviyoFormCatch(Component)
  const HydratedComponent = withHydration(WithKlaviyoForm, options)

  const PreppedComponent = ({ env = {}, ...props }) => {
    const railsContext = props.railsContext || EMPTY_OBJECT
    const useLocation = isSSR ? staticLocationHook((railsContext.pathname || '').replace('', '')) : browserLocationHook
    const appConfig = useMemo(
      () => ({
        env,
        railsContext,
        user: props.user,
      }),
      [env, railsContext, props.user],
    )

    // Remove server-side injected CSS when component mounts
    useEffect(() => {
      const jssStyles = document.querySelector('#jss-server-side')
      if (jssStyles) {
        jssStyles.parentElement.removeChild(jssStyles)
      }
    }, [])

    return (
      <StoreProvider store={store}>
        <ErrorBoundary env={env}>
          <ThemeProvider theme={muiTheme}>
            <StylesProvider>
              <Router base='' hook={useLocation}>
                <AppConfigContext.Provider value={appConfig}>
                  <HydratedComponent {...props} env={env} />
                </AppConfigContext.Provider>
              </Router>
            </StylesProvider>
          </ThemeProvider>
        </ErrorBoundary>
      </StoreProvider>
    )
  }

  return withStyledSsr(PreppedComponent)
}

export default withApp
